import { faMoneyBillTransfer } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddIcon from '@mui/icons-material/Add';
import { Button, Grid, Skeleton, Stack, styled, TableCell, TableRow, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { green, orange, red } from '@mui/material/colors';
import dinero from 'dinero.js';
import moment from 'moment';
import React, { useContext, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import useMetricsDailyStats, { OnlyFansPayout } from '../../hooks/useMetricsDailyStats';
import useMetricsDailyStatsEarningsAll from '../../hooks/useMetricsDailyStatsEarningsAll';
import useSubscriberCategories from '../../hooks/useSubscriberCategories';
import useSubscriberStatsNewSubscribersCount from '../../hooks/useSubscriberStatsNewSubscribersCount';
import useTransactionsDailyStats, { OnlyFansTransactionsDailyStats } from '../../hooks/useTransactionsDailyStats';
import { SettingsContext } from '../../store/SettingsContext';
import { buildURL, creditStatus, d2f, getServiceStatusColor } from '../../utils/common';
import UsernameWithAvatar from '../common/UsernameWithAvatar';
import ValueAndTrend from '../subscriber/ValueAndTrend';

const TableCellStyles = styled(TableCell)(({ theme }) => ({
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    '&:first-of-type': {
        paddingLeft: theme.spacing(2),
    },
    '&:last-of-type': {
        paddingRight: theme.spacing(2),
    },
}));

const SubscriberServicesStatus = ({ subscriber }: any) => {
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();

    return (
        <Grid container spacing={0.5} alignItems="center" justifyContent="flex-start">
            {settingsContext &&
                settingsContext.services &&
                Object.keys(settingsContext.services)
                    .filter(
                        (key: string) => settingsContext.services[key].visible === true,
                        // &&
                        // isServiceActive(subscriber, key, settingsContext.services[key].alwaysActive),
                    )
                    .map((key: string) => (
                        <Grid item xs={'auto'} key={`${subscriber._id}_${key}`}>
                            <Tooltip title={settingsContext.services[key].name.toUpperCase()} placement="top">
                                <Link
                                    to={`${buildURL(`/subscribers/${subscriber._id}`, settingsContext.services[key].homeUrl || '')}`}
                                    style={{ textDecoration: 'none', color: theme.palette.primary.main, display: 'inline-flex' }}
                                >
                                    {settingsContext.services[key].icon(
                                        getServiceStatusColor(
                                            subscriber,
                                            settingsContext.services[key].serviceNameKey || key,
                                            settingsContext.services[settingsContext.services[key].serviceNameKey || key].alwaysActive,
                                        ),
                                    )}
                                </Link>
                            </Tooltip>
                        </Grid>
                    ))}
        </Grid>
    );
};

const status: any = {
    good: {
        color: green[900],
        background: green[100],
    },
    low: {
        color: orange[900],
        background: orange[100],
    },
    none: {
        color: red[900],
        background: red[100],
    },
};

type Props = {
    subscriber: any;
    setStats: (stats: any) => void;
    refetchSubscribers: () => void;
    openTransferCreditDialog: (subscriber: any) => void;
    amountType: 'gross' | 'net';
    todayDate: string;
    comparisonDate: string;
    onUpdateDailyTransactionStats: (
        subscriberId: string,
        stats: { today: OnlyFansTransactionsDailyStats | null; comparison: OnlyFansTransactionsDailyStats | null },
    ) => void;
    onUpdateNewSubscribersToday: (subscriberId: string, count: number) => void;
    onUpdateSummaryLoadProgress: (
        subscriberId: string,
        progress: { earnings: boolean; today: boolean; comparison: boolean; newSubscribers: boolean },
    ) => void;
};

const DashboardMyAccountsSubscriberCardLarge = React.memo((props: Props) => {
    const {
        subscriber,
        setStats,
        refetchSubscribers,
        openTransferCreditDialog,
        amountType,
        todayDate,
        comparisonDate,
        onUpdateDailyTransactionStats,
        onUpdateNewSubscribersToday,
        onUpdateSummaryLoadProgress,
    } = props;
    const sharedCredit: number = subscriber.financial && subscriber.financial.sharedCredit ? d2f(subscriber.financial.sharedCredit) : 0;
    const creditStatusName: string = creditStatus(sharedCredit);
    const timezone: string = useMemo(() => moment.tz.guess(), []);
    const startDate: Date = useMemo(() => moment().subtract(2, 'days').startOf('day').toDate(), []);
    const endDate: Date = useMemo(() => moment().endOf('day').toDate(), []);

    const { data: dailyStats, isFetching: dailyStatsLoading } = useMetricsDailyStats(
        startDate,
        endDate,
        timezone,
        subscriber?._id && subscriber.isLoggedIn ? subscriber._id : null,
    );
    const { data: latestEarnings, isLoading: isLatestEarningsLoading } = useMetricsDailyStatsEarningsAll(
        'latest',
        subscriber?._id && subscriber.isLoggedIn ? subscriber._id : null,
    );
    const { data: latestEarningsMinusOneDay, isLoading: isLatestEarningsMinusOneDayLoading } = useMetricsDailyStatsEarningsAll(
        'oneBeforeLatest',
        subscriber?._id && subscriber.isLoggedIn ? subscriber._id : null,
    );
    const { data: todayDailyStats, isLoading: isTodayDailyStatsLoading } = useTransactionsDailyStats(
        subscriber?._id && subscriber.isLoggedIn ? subscriber._id : null,
        todayDate,
        timezone,
    );
    const { data: comparisonDailyStats, isLoading: isComparisonDailyStatsLoading } = useTransactionsDailyStats(
        subscriber?._id && subscriber.isLoggedIn ? subscriber._id : null,
        comparisonDate,
        timezone,
    );
    const gmtTodayStartDate = useMemo(() => moment.utc().startOf('day').toDate(), []);
    const gmtTodayEndDate = useMemo(() => moment.utc().toDate(), []);
    const { data: newSubscribersTodayCount, isLoading: isNewSubscribersTodayCountLoading } = useSubscriberStatsNewSubscribersCount(
        subscriber?._id && subscriber.isLoggedIn ? subscriber._id : null,
        gmtTodayStartDate,
        gmtTodayEndDate,
        'new',
    );
    const { data: categories, isFetching: categoriesLoading } = useSubscriberCategories();

    const category = useMemo(() => {
        if (categoriesLoading || !subscriber.cognito || !subscriber.cognito.category) {
            return undefined;
        }

        return categories && categories.find(c => c._id === subscriber.cognito.category);
    }, [categories, categoriesLoading, subscriber.cognito]);

    useEffect(() => {
        if (!subscriber?._id) {
            return;
        }

        setStats((prevStats: Record<string, any>) => {
            const existing = Array.isArray(prevStats[subscriber._id]) ? prevStats[subscriber._id] : [{}, {}];
            const nextStats = [...existing];

            nextStats[0] = {
                ...(nextStats[0] || {}),
                ...(dailyStats?.[0] || {}),
                earnings: latestEarnings || undefined,
            };
            nextStats[1] = {
                ...(nextStats[1] || {}),
                ...(dailyStats?.[1] || {}),
                earnings: latestEarningsMinusOneDay || undefined,
            };

            return {
                ...prevStats,
                [subscriber._id]: nextStats,
            };
        });
    }, [dailyStats, latestEarnings, latestEarningsMinusOneDay, setStats, subscriber]);

    useEffect(() => {
        if (!subscriber?._id) {
            return;
        }

        onUpdateDailyTransactionStats(subscriber._id, {
            today: todayDailyStats || null,
            comparison: comparisonDailyStats || null,
        });
    }, [subscriber, todayDailyStats, comparisonDailyStats, onUpdateDailyTransactionStats]);

    useEffect(() => {
        if (!subscriber?._id) {
            return;
        }

        const numeric = Number(newSubscribersTodayCount ?? 0);
        onUpdateNewSubscribersToday(subscriber._id, Number.isFinite(numeric) ? numeric : 0);
    }, [subscriber, newSubscribersTodayCount, onUpdateNewSubscribersToday]);

    useEffect(() => {
        if (!subscriber?._id) {
            return;
        }

        onUpdateSummaryLoadProgress(subscriber._id, {
            earnings: !isLatestEarningsLoading && !isLatestEarningsMinusOneDayLoading,
            today: !isTodayDailyStatsLoading,
            comparison: !isComparisonDailyStatsLoading,
            newSubscribers: !isNewSubscribersTodayCountLoading,
        });
    }, [
        subscriber,
        isLatestEarningsLoading,
        isLatestEarningsMinusOneDayLoading,
        isTodayDailyStatsLoading,
        isComparisonDailyStatsLoading,
        isNewSubscribersTodayCountLoading,
        onUpdateSummaryLoadProgress,
    ]);

    const getValue = (earnings: OnlyFansPayout.PayoutsStats | null | undefined) => {
        if (!earnings) {
            return undefined;
        }

        if (amountType === 'gross') {
            return Number(earnings.list?.total?.all?.total_gross || 0);
        }

        return Number(earnings.list?.total?.all?.total_net || 0);
    };

    const getDailyAmount = (grossOrNetAmount: unknown): number => {
        const numeric = Number(grossOrNetAmount);

        return Number.isFinite(numeric) ? numeric : 0;
    };

    return (
        <TableRow
            sx={{
                ...(category &&
                    category.color && {
                        backgroundImage: `linear-gradient(90deg, ${category.color} 0%, ${category.color} 8px, transparent 8px);`,
                    }),
            }}
        >
            <TableCellStyles>
                <UsernameWithAvatar subscriber={subscriber} category={category} refetchSubscribers={refetchSubscribers} />
            </TableCellStyles>
            <TableCellStyles width={118}>
                <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent={'center'}>
                    <Grid item xs={12} textAlign="center">
                        <Typography variant="h6" color={status[creditStatusName].color}>
                            {dinero({
                                amount: Math.trunc(sharedCredit * 100),
                                currency: 'GBP',
                            }).toFormat()}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={0}>
                            <Tooltip title="Top Up Credit" placement="top">
                                <Button
                                    variant="contained"
                                    component={Link}
                                    to={`/subscribers/${subscriber._id}/account/sharedCreditCheckout`}
                                    color="success"
                                >
                                    <AddIcon />
                                </Button>
                            </Tooltip>
                            <Tooltip title="Transfer Credit" placement="top">
                                <span>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        size="large"
                                        onClick={() => {
                                            openTransferCreditDialog(subscriber);
                                        }}
                                        disabled={sharedCredit === 0}
                                    >
                                        <FontAwesomeIcon icon={faMoneyBillTransfer} />
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Grid>
                </Grid>
            </TableCellStyles>
            <TableCellStyles width={62}>
                <ValueAndTrend
                    loading={dailyStatsLoading}
                    value={dailyStats && dailyStats[0] && dailyStats[0].counters ? dailyStats[0].counters.subscribers.active : undefined}
                    previousValue={
                        dailyStats && dailyStats.length > 1 && dailyStats[1] && dailyStats[1].counters
                            ? dailyStats[1].counters.subscribers.active
                            : undefined
                    }
                    type={'number'}
                    fractionDigits={0}
                    separateLines
                    textAlign="center"
                />
            </TableCellStyles>
            <TableCellStyles width={62}>
                <ValueAndTrend
                    loading={dailyStatsLoading}
                    value={dailyStats && dailyStats[0] && dailyStats[0].counters ? dailyStats[0].counters.subscriptions.active : undefined}
                    previousValue={
                        dailyStats &&
                        dailyStats.length > 1 &&
                        dailyStats[1].counters &&
                        dailyStats[1].counters.subscriptions &&
                        dailyStats[1].counters.subscriptions.active
                            ? dailyStats[1].counters.subscriptions.active
                            : undefined
                    }
                    type={'number'}
                    fractionDigits={0}
                    separateLines
                    textAlign="center"
                />
            </TableCellStyles>
            <TableCellStyles width={62}>
                <ValueAndTrend
                    loading={dailyStatsLoading}
                    value={dailyStats && dailyStats[0] && dailyStats[0].performerTop ? dailyStats[0].performerTop : undefined}
                    previousValue={
                        dailyStats && dailyStats.length > 1 && dailyStats[1].performerTop ? dailyStats[1].performerTop : undefined
                    }
                    type={'number'}
                    logic="less"
                    fractionDigits={2}
                    separateLines
                    textAlign="center"
                />
            </TableCellStyles>
            <TableCellStyles width={132}>
                <ValueAndTrend
                    loading={isLatestEarningsLoading || isLatestEarningsMinusOneDayLoading}
                    value={getValue(latestEarnings)}
                    previousValue={getValue(latestEarningsMinusOneDay)}
                    type={'money'}
                    fractionDigits={2}
                    separateLines
                    textAlign="center"
                />
            </TableCellStyles>
            <TableCellStyles width={178}>
                <Stack spacing={0.5} alignItems="center">
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        {isTodayDailyStatsLoading ? (
                            <Skeleton variant="text" width={64} />
                        ) : (
                            <Typography variant="h6" color={'primary.main'} textAlign="center">
                                {dinero({
                                    amount: Math.round(
                                        getDailyAmount(amountType === 'gross' ? todayDailyStats?.totalAmount : todayDailyStats?.totalNet) *
                                            100,
                                    ),
                                    currency: 'USD',
                                }).toFormat('$0,0.00')}
                            </Typography>
                        )}
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        {isComparisonDailyStatsLoading ? (
                            <Skeleton variant="text" width={64} />
                        ) : (
                            <Typography variant="h6" color={'text.secondary'} textAlign="center">
                                {dinero({
                                    amount: Math.round(
                                        getDailyAmount(
                                            amountType === 'gross' ? comparisonDailyStats?.totalAmount : comparisonDailyStats?.totalNet,
                                        ) * 100,
                                    ),
                                    currency: 'USD',
                                }).toFormat('$0,0.00')}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </TableCellStyles>
            <TableCellStyles width={92} align="center">
                {isNewSubscribersTodayCountLoading ? (
                    <Skeleton variant="text" width={32} sx={{ mx: 'auto' }} />
                ) : (
                    <Typography variant="h6" color="primary.main" textAlign="center">
                        {Number(newSubscribersTodayCount ?? 0).toLocaleString()}
                    </Typography>
                )}
            </TableCellStyles>
            <TableCellStyles width={188}>
                <SubscriberServicesStatus subscriber={subscriber} />
            </TableCellStyles>
        </TableRow>
    );
});

export default DashboardMyAccountsSubscriberCardLarge;
